import {
  TimeWindow,
  makeStartAndFinishTimeValues
} from '@gfe/core/models/entities/implementations/time-window';

import {
  getFormatedDateFromDate,
  getFormatedDateTimeFromDate,
  getFormatedTimeFromDate
} from '@core/utils/date-utils';
import { formatQuantity } from '@core/utils/quantity-utils';
import { compareAlphabetically, isNil } from '@core/utils/shared-utils';
import {
  ISO8601_DURATION_REGEX,
  formatDuration,
  formatDurationAsHms,
  formatDurationFromIso,
  fromSecondsToDuration,
  parseFormattedDuration,
  parseISO8601Duration
} from '@core/utils/time-utils';

import { TimeValue } from '@shared/components/dropdown/template/timepicker-dropdown/timepicker-dropdown.component';

import { TableCellDataType } from './table-cell-data-type';
import { isUidListItem } from './uid-list/uid-list-item.model';

export interface WindowValues<T> {
  start: T;
  finish: T;
}

export interface AddressValue {
  city: string | null;
  country: string | null;
  county: string | null;
  house_number: string | null;
  label: string | null;
  postal_code: string | null;
  state: string | null;
  street: string | null;
}

export function processAddressValue(rawValue: unknown): string {
  if (isNil(rawValue)) return '';
  const addr = rawValue as AddressValue;
  if (addr.label) return addr.label;
  const parts = [
    addr.house_number,
    addr.street,
    addr.postal_code,
    addr.city,
    addr.country
  ].filter(Boolean);
  return parts.join(', ');
}

export function processStringValue(rawValue: unknown): string {
  if (isNil(rawValue)) {
    return '';
  }
  return rawValue as string;
}

/**
 * Format a numeric quantity for display: locale thousands grouping while
 * preserving the value's own decimals (up to the Intl maximum of 20 digits, so
 * no silent rounding). Non-numeric input is coerced when possible, otherwise
 * returned verbatim so malformed values stay visible rather than showing "NaN".
 */
export function processNumberValue(rawValue: unknown, locale?: string): string {
  if (isNil(rawValue) || rawValue === '') {
    return '';
  }
  const num = typeof rawValue === 'number' ? rawValue : Number(rawValue);
  if (!isFinite(num)) {
    return String(rawValue);
  }
  return numberFormatterFor(locale).format(num);
}

const numberFormatters = new Map<string, Intl.NumberFormat>();

/**
 * One formatter per locale, for the same reason as the date helpers in `date-utils`:
 * `toLocaleString(locale, options)` builds a native ICU formatter per call, and number cells
 * format on every change detection pass.
 */
function numberFormatterFor(locale?: string): Intl.NumberFormat {
  const key = locale ?? '';
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 20 });
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Whether a value is a non-null object (plain object or array). These are the
 * values that, rendered through the default string path, would stringify to
 * "[object Object]" — so the table falls them back to the JSON cell instead.
 */
export function isComplexCellValue(rawValue: unknown): boolean {
  return typeof rawValue === 'object' && rawValue !== null;
}

/**
 * Compact, single-line JSON for inline display in a cell. Strings are returned
 * as-is (no surrounding quotes); nil becomes an empty string. Used as the
 * fallback so object/array values show their content rather than "[object Object]".
 */
export function processJsonValue(rawValue: unknown): string {
  if (isNil(rawValue)) {
    return '';
  }
  if (typeof rawValue === 'string') {
    return rawValue;
  }
  try {
    return JSON.stringify(rawValue);
  } catch {
    return String(rawValue);
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Pretty-printed JSON, HTML-escaped and wrapped in a <pre> so it renders with
 * preserved indentation inside the shared tooltip (which injects content via
 * [innerHTML] after sanitization). Returns '' for nil.
 */
export function formatJsonForTooltip(rawValue: unknown): string {
  if (isNil(rawValue)) {
    return '';
  }
  let pretty: string;
  try {
    pretty = JSON.stringify(rawValue, null, 2);
  } catch {
    pretty = String(rawValue);
  }
  return `<pre class="json-cell-tooltip">${escapeHtml(pretty)}</pre>`;
}

export function processBooleanValue(rawValue: unknown): boolean | undefined {
  if (isNil(rawValue)) {
    return false;
  }
  return Boolean(rawValue);
}

export function processUidValue(rawValue: unknown): string | undefined {
  if (isNil(rawValue)) {
    return undefined;
  }
  return rawValue as string;
}

export function processEnumValue(rawValue: unknown): string[] | undefined {
  if (isNil(rawValue) || rawValue === '') {
    return undefined;
  }
  if (Array.isArray(rawValue)) {
    return processUidListValue(rawValue);
  }
  return [String(rawValue)];
}

export function processUidListValue(rawValue: unknown): string[] | undefined {
  if (Array.isArray(rawValue)) {
    return Array.from(rawValue)
      .map((item) => (isUidListItem(item) ? item.uid : processUidValue(item)))
      .filter((val) => !isNil(val));
  }
  return undefined;
}

export function processKeyValueMap(rawValue: unknown): Record<string, number> {
  if (isNil(rawValue)) {
    return {};
  }
  // Sort keys alphabetically so dict-like values (e.g. capacity labels) are
  // displayed in a consistent order everywhere they are rendered.
  const map = rawValue as Record<string, number>;
  return Object.fromEntries(
    Object.entries(map).sort(([keyA], [keyB]) => compareAlphabetically(keyA, keyB))
  );
}

/**
 * How a duration is rendered: `units` is the unit-suffixed form ("9h 7min 51s"),
 * `hms` the zero-padded clock form ("09:07:51").
 */
export type DurationDisplayFormat = 'units' | 'hms';

export function processDurationValue(
  rawValue: unknown,
  format: DurationDisplayFormat = 'units'
): string {
  if (isNil(rawValue)) {
    return '';
  }

  if (Array.isArray(rawValue)) {
    if (rawValue.length === 0) {
      return '';
    }
    return rawValue.map((item) => processDurationValue(item, format)).join(' | ');
  }

  if (typeof rawValue === 'string') {
    const matches = rawValue.match(ISO8601_DURATION_REGEX);
    if (matches) {
      return format === 'hms'
        ? formatDurationAsHms(parseISO8601Duration(rawValue))
        : formatDurationFromIso(rawValue);
    }
    // Columns whose renderHook already formats the duration (e.g. route KPIs)
    // hand us the unit-suffixed string, which the clock form has to re-parse.
    const parsed = parseFormattedDuration(rawValue);
    if (!isNil(parsed)) {
      return format === 'hms' ? formatDurationAsHms(parsed) : rawValue;
    }
  } else if (typeof rawValue === 'number') {
    const duration = fromSecondsToDuration(rawValue);
    return format === 'hms' ? formatDurationAsHms(duration) : formatDuration(duration);
  }

  return format === 'hms' ? '00:00:00' : '0s';
}

export interface DurationSegment {
  /** Numeric part of a duration token (e.g. "30"), or a separator such as "|". */
  value: string;
  /** Unit label of a duration token (e.g. "h", "min", "s"); absent for separators. */
  unit?: string;
}

/**
 * Splits a formatted duration string (e.g. "1h 30min 5s | 2h") into value/unit
 * segments so the renderer can style the numbers and unit labels differently.
 * The "|" separator between multiple durations is returned as a unit-less segment.
 */
export function splitDurationParts(formatted: string): DurationSegment[] {
  if (!formatted) {
    return [];
  }
  return formatted.split(' ').map((token) => {
    const match = /^(-?\d+(?:\.\d+)?)(.*)$/.exec(token);
    if (!match) {
      return { value: token };
    }
    return { value: match[1], unit: match[2] };
  });
}

export type WindowType = 'time' | 'date' | 'datetime';
export function processWindowValue(
  rawValue: unknown,
  type: WindowType = 'time',
  locale?: string
): string {
  if (isNil(rawValue)) {
    return '';
  }

  if (Array.isArray(rawValue)) {
    if (rawValue.length === 0) {
      return '';
    }

    const first_date = new Date(rawValue[0].start).toDateString();
    const allSameDay = rawValue.every(
      (w) =>
        new Date(w.start).toDateString() === first_date &&
        new Date(w.finish).toDateString() === first_date
    );

    // Only write date once if all the same
    if (allSameDay) {
      const datePart = getFormatedDateFromDate(new Date(rawValue[0].start), locale);
      const ranges = rawValue.map(
        (w) =>
          `${getFormatedTimeFromDate(new Date(w.start), locale)} → ${getFormatedTimeFromDate(new Date(w.finish), locale)}`
      );
      return `${datePart}, ${ranges.join(' | ')}`;
    }

    // Otherwise show all windows explicitly
    return rawValue.map((item) => processWindowValue(item, type, locale)).join(' | ');
  }

  if (typeof rawValue === 'object' && rawValue !== null) {
    const valueObj = rawValue as Record<string, unknown>;
    const startRaw = valueObj['start'];
    const finishRaw = valueObj['finish'];

    if (!isNil(startRaw) && !isNil(finishRaw)) {
      let startStr: string;
      let finishStr: string;

      const startDate = new Date(startRaw as string);
      const finishDate = new Date(finishRaw as string);

      if (type === 'time') {
        startStr = getFormatedTimeFromDate(startDate, locale);
        finishStr = getFormatedTimeFromDate(finishDate, locale);
      } else if (type === 'date') {
        startStr = getFormatedDateFromDate(startDate, locale);
        finishStr = getFormatedDateFromDate(finishDate, locale);
      } else {
        if (startDate.toDateString() === finishDate.toDateString()) {
          finishStr = getFormatedTimeFromDate(finishDate, locale);
        } else {
          finishStr = getFormatedDateTimeFromDate(finishDate, locale);
        }

        startStr = getFormatedDateTimeFromDate(startDate, locale);
      }

      return `${startStr} → ${finishStr}`;
    }

    const durationRaw = valueObj['duration'];
    if (!isNil(startRaw) && !isNil(durationRaw)) {
      const timeValues = makeStartAndFinishTimeValues(rawValue as TimeWindow);
      if (isNil(timeValues)) {
        return '';
      }

      const [startTime, finishTime] = timeValues;
      const startStr = `${startTime.hours.toString().padStart(2, '0')}:${startTime.minutes.toString().padStart(2, '0')}`;
      const finishStr = `${finishTime.hours.toString().padStart(2, '0')}:${finishTime.minutes.toString().padStart(2, '0')}`;

      return `${startStr} → ${finishStr}`;
    }
  }

  return '';
}

export function processDatetimeValue(
  rawValue: unknown
): Date | WindowValues<Date> | undefined {
  if (isNil(rawValue)) {
    return undefined;
  }

  if (Array.isArray(rawValue)) {
    const dateValues: Date[] = [];

    for (const item of rawValue) {
      dateValues.push(new Date(item as string));
    }

    if (dateValues.length === 2) {
      return { start: dateValues[0], finish: dateValues[1] };
    }
  } else if (typeof rawValue === 'string' || rawValue instanceof Date) {
    return new Date(rawValue as string);
  }

  return undefined;
}

export function formatTime(value: unknown): string {
  const timeValue = processTimeValue(value) as TimeValue | undefined;
  if (!timeValue || timeValue.hours === undefined || timeValue.minutes === undefined) {
    return '';
  }
  const pad = (num: number) => num.toString().padStart(2, '0');
  return `${pad(timeValue.hours)}:${pad(timeValue.minutes)}`;
}

const REFERENCE_CELL_TYPES: ReadonlySet<TableCellDataType | undefined> = new Set<
  TableCellDataType | undefined
>(['uid', 'uid-list']);

/** Cell types that show entity references, so offer the name/uid toggle and name resolution. */
export function isReferenceCellType(type: TableCellDataType | undefined): boolean {
  return REFERENCE_CELL_TYPES.has(type);
}

/** The referenced uids a reference cell's value holds, a single uid or a uid list. */
export function referenceUidsOf(value: unknown): unknown[] {
  if (Array.isArray(value)) return processUidListValue(value) ?? [];
  return isNil(value) ? [] : [value];
}

export function formatCellValue(
  value: unknown,
  type?: TableCellDataType,
  lang?: string
): string {
  if (value === null || value === undefined) return '';

  switch (type) {
    case 'duration':
      return processDurationValue(value);
    case 'durations':
      return processDurationValue(value);
    case 'time': {
      const timeValue = processTimeValue(value) as TimeValue | undefined;
      if (!timeValue || !('hours' in timeValue)) return '';
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${pad(timeValue.hours)}:${pad(timeValue.minutes)}`;
    }
    case 'date': {
      const dateValue = processDatetimeValue(value) as Date | undefined;
      if (!dateValue) return '';
      return getFormatedDateFromDate(dateValue, lang);
    }
    case 'datetime': {
      const dateValue = processDatetimeValue(value) as Date | undefined;
      if (!dateValue) return '';
      return getFormatedDateTimeFromDate(dateValue, lang);
    }
    case 'time-window':
      return processWindowValue(value, 'time', lang);
    case 'date-window':
      return processWindowValue(value, 'date', lang);
    case 'datetime-window':
      return processWindowValue(value, 'datetime', lang);
    case 'datetime-windows':
      return processWindowValue(value, 'datetime', lang);
    case 'uid':
      return processUidValue(value) ?? '';
    case 'enum':
    case 'uid-list':
    case 'text-list': {
      const list = processUidListValue(value);
      return list ? list.join(', ') : '';
    }
    case 'multi-select':
      return Array.isArray(value) ? value.join(', ') : String(value);
    case 'string-number-map': {
      const map = processKeyValueMap(value);
      return Object.entries(map)
        .map(([k, v]) => `${k}: ${formatMapNumber(v)}`)
        .join(', ');
    }
    case 'address':
      return processAddressValue(value);
    case 'number':
      return processNumberValue(value, lang);
    case 'json':
      return processJsonValue(value);
    default:
      return isComplexCellValue(value) ? processJsonValue(value) : String(value);
  }
}

/**
 * A time only counts when both halves are real numbers. Returning a half-built
 * `{ hours, minutes }` instead would hand every caller an object that passes an
 * `'hours' in value` check and then throws on `hours.toString()`.
 */
function readTimeValue(rawValue: unknown): TimeValue | undefined {
  if (typeof rawValue !== 'object' || rawValue === null) {
    return undefined;
  }
  const { hours, minutes } = rawValue as Record<string, unknown>;
  return Number.isFinite(hours) && Number.isFinite(minutes)
    ? { hours: hours as number, minutes: minutes as number }
    : undefined;
}

export function processTimeValue(
  rawValue: unknown
): TimeValue | WindowValues<TimeValue> | undefined {
  if (isNil(rawValue)) {
    return undefined;
  }

  const naiveTimeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/;

  if (typeof rawValue === 'string') {
    const match = naiveTimeRegex.exec(rawValue);
    return isNil(match)
      ? undefined
      : { hours: parseInt(match[1], 10), minutes: parseInt(match[2], 10) };
  }

  if (rawValue instanceof Date) {
    return { hours: rawValue.getHours(), minutes: rawValue.getMinutes() };
  }

  // Before the object branch: an array is an object, so testing it second would read
  // `hours` off the array itself and never reach a window.
  if (Array.isArray(rawValue)) {
    const [start, finish] = rawValue.map(readTimeValue);
    return rawValue.length === 2 && start && finish ? { start, finish } : undefined;
  }

  if (typeof rawValue === 'object') {
    const { start, finish } = rawValue as Record<string, unknown>;
    if (!isNil(start) || !isNil(finish)) {
      const startValue = readTimeValue(start);
      const finishValue = readTimeValue(finish);
      return startValue && finishValue
        ? { start: startValue, finish: finishValue }
        : undefined;
    }
    return readTimeValue(rawValue);
  }

  return undefined;
}

/**
 * Renders one value of a string→number map (capacity totals and similar KPI maps)
 * at the precision the number actually carries, so solver float noise does not
 * surface as spurious decimals. Non-numeric values pass through verbatim.
 */
export function formatMapNumber(value: unknown): string {
  return typeof value === 'number' ? formatQuantity(value) : String(value);
}
